<template>
  <div id="jci-program-list" class="page-container">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
      :LoadingText="OverlayLoadingText"
    ></overlay-loading-progress>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8">
              <div class="d-flex align-center">
                <div class="header-icon-box">
                  <v-icon large color="white"
                    >mdi-calendar-multiple-check</v-icon
                  >
                </div>
                <div class="ml-6">
                  <h1 class="header-title white--text mb-1">
                    {{ YearName }} - {{ PageTitle }}
                  </h1>
                  <p class="header-subtitle white--text mb-0">
                    {{
                      PageDescription ||
                      "Browse and register for national-level events scheduled for the year 2025."
                    }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <div class="d-flex justify-end flex-wrap gap-2">
                <v-btn
                  @click.prevent="refreshPageData"
                  color="rgba(255,255,255,0.2)"
                  elevation="0"
                  rounded
                  large
                  class="action-btn"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="addRecordPrompt" persistent scrollable>
      <ngb-team-members-modify
        pageDescription="Assign NGB members"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        v-if="addRecordPrompt"
      ></ngb-team-members-modify>
    </v-dialog>

    <v-dialog v-model="editRecordPrompt" persistent scrollable>
      <ngb-team-members-edit
        pageDescription="Edit NGB members"
        :editRecordPrompt="editRecordPrompt"
        :recordData="selectedData"
        @hideEditRecordPrompt="hideEditRecordPrompt"
        v-if="editRecordPrompt"
      ></ngb-team-members-edit>
    </v-dialog>

    <!-- Loading State -->
    <div v-if="LoadingFlag" class="text-center py-12">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <p class="mt-4 text-h6 primary--text">Loading events...</p>
    </div>

    <!-- No Records Alert -->
    <v-card elevation="8" class="mb-4" v-if="ResultFlag && rows.length === 0">
      <v-card-text class="pa-6">
        <v-alert type="info" dense outlined icon="mdi-information" prominent>
          <v-alert-title>No Events Found</v-alert-title>
          There are no events available at the moment. Please check back later.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Events Grid -->
    <div v-if="rows.length > 0" class="events-section">
      <!-- Stats Alert -->
      <v-alert type="info" prominent border="left" class="stats-alert mb-6">
        <template v-slot:prepend>
          <v-icon>mdi-calendar-multiple-check</v-icon>
        </template>
        <div class="alert-content">
          <p class="alert-title">Total Events Available</p>
          <h2 class="alert-number">{{ rows.length }}</h2>
          <p class="alert-subtitle">Ready for registration</p>
        </div>
      </v-alert>
      <br />
      <!-- Events Grid -->
      <v-row wrap>
        <v-col
          v-for="item in rows"
          :key="item.ProgramId"
          cols="12"
          lg="4"
          sm="6"
          md="6"
          class="mb-2"
        >
          <v-card elevation="12" class="event-card" hover>
            <!-- Card Header -->
            <div class="event-card-header">
              <div class="event-icon-box">
                <v-icon large color="white">mdi-calendar-check</v-icon>
              </div>
            </div>

            <!-- Card Content -->
            <v-card-text class="event-card-content">
              <h2 class="event-title mb-3">
                {{ item.ProgramNameTxt }}
              </h2>

              <!-- Event Details -->
              <div class="event-detail mb-2">
                <v-icon small class="detail-icon mr-2"
                  >mdi-calendar-range</v-icon
                >
                <span class="detail-label">Date:</span>
                <span class="detail-value">{{ item.ProgramDateTxt }}</span>
              </div>

              <div class="event-detail mb-2">
                <v-icon small class="detail-icon mr-2">mdi-home-group</v-icon>
                <span class="detail-label">Host:</span>
                <span class="detail-value">{{ item.HostTxt }}</span>
              </div>

              <div class="event-detail mb-3">
                <v-icon small class="detail-icon mr-2">mdi-map-marker</v-icon>
                <span class="detail-label">Venue:</span>
                <span class="detail-value">{{ item.VenueDetails }}</span>
              </div>
            </v-card-text>

            <!-- Card Actions -->
            <v-card-actions class="event-card-actions">
              <router-link
                v-if="item.RegistrationPagePathTxt != ''"
                :to="item.RegistrationPagePathTxt"
                class="action-link"
              >
                <v-btn
                  elevation="6"
                  rounded
                  small
                  color="#667eea"
                  class="action-btn register-btn white--text font-weight-bold"
                  block
                >
                  <v-icon small left>mdi-clipboard-text</v-icon>
                  Register
                </v-btn>
              </router-link>

              <v-btn
                elevation="6"
                rounded
                small
                color="#10b981"
                class="action-btn edit-btn white--text font-weight-bold"
                @click="editData(item)"
                v-if="item.DesignationId != 4 && editFlag"
                block
              >
                <v-icon small left>mdi-pencil-outline</v-icon>
                Edit
              </v-btn>

              <v-btn
                elevation="6"
                rounded
                small
                color="#f44336"
                class="action-btn delete-btn white--text font-weight-bold"
                @click="deleteData(item)"
                v-if="DeleteFlag"
                block
              >
                <v-icon small left>mdi-trash-can-outline</v-icon>
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import NgbTeamMembersModify from "@/view/pages/erp/team-members/ngb/NgbTeamMembersModify.vue";
import NgbTeamMembersEdit from "@/view/pages/erp/team-members/ngb/NgbTeamMembersEdit.vue";

export default {
  mixins: [common],
  components: {
    NgbTeamMembersModify,
    NgbTeamMembersEdit,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      DeleteFlag: true,

      OverlayLoadingFlag: false,
      OverlayLoadingText: "",

      rows: [],
      tableSelectFlag1: false,
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      editFlag: false,
      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      zone: {},
      lom: {},

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      FromZoneCodeRules: [(v) => !!v || "Zone is required"],
      FromZoneCode: "",
      FromZoneCodeOptions: [],
      FromZoneCodeOptionsLoading: false,

      FromLomCodeRules: [(v) => !!v || "LO is required"],
      FromLomCode: "",
      FromLomCodeOptions: [],
      FromLomCodeOptionsLoading: false,

      ToZoneCodeRules: [(v) => !!v || "Zone is required"],
      ToZoneCode: "",
      ToZoneCodeOptions: [],
      ToZoneCodeOptionsLoading: false,

      ToLomCodeRules: [(v) => !!v || "LO is required"],
      ToLomCode: "",
      ToLomCodeOptions: [],
      ToLomCodeOptionsLoading: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],
      SearchIdOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "By LO" },
        { value: 2, text: "By Membership Id" },
      ],

      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      YearHalfId: "",
      YearHalfIdRules: [(v) => !!v || "Year Half is required"],
      YearHalfIdOptions: [
        { value: "", text: "--Select Half--" },
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      YearIdRules: [(v) => !!v || "Year is required"],
      YearId: "",
      YearIdOptions: [],
      YearIdOptionsLoading: false,

      ApprovalDescription: "",
      ApprovalDescriptionRules: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "jci_events",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getTableRecords();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.getTableRecords();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.getTableRecords();
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? "" : LomCode;
      console.log("LomCode=" + LomCode);

      if (LomCode != null) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/my-lists";
        var upload = {
          UserInterface: 1,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        this.rows = [];
        this.ResultFlag = false;
        this.LoadingFlag = true;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);
            records = response.data.records;
            console.log({ records });
            var TableOptions = records.TableOptions;

            if (flag == 1) {
              thisIns.rows = records.TableData;
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableSelectFlag1 = TableOptions.TableSelectFlag;
              thisIns.DeleteFlag = TableOptions.TableDeleteFlag;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Your session is expired. Kindly login again. ";
        console.log("error=" + message);
      }
    },
    getZoneCodeOptions(source, destination) {
      console.log("getZoneCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      var CurrentYearId = this.CurrentYearId;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: CurrentYearId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomCodeOptions(source, destination, ZoneCode) {
      console.log("getLomCodeOptions called");
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", ZoneCode=" +
          ZoneCode
      );
      var CurrentYearId = this.CurrentYearId;
      if (ZoneCode != "" && CurrentYearId != "") {
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: CurrentYearId,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "Zone should not be empty";
        this.toast("error", message);
      }
    },
    deleteData(tr) {
      console.log("deleteData called");
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        this.deleteRecord(tr);
      } else {
        var message = "Kindly select one member to delete";
        this.toast("error", message);
      }
    },
    deleteRecord(tr) {
      // add todo in store and clear all fields in dialog
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/member-delete";
        var upload = {
          UserInterface: 1,
          OrganizationType: 1,
          Year: this.JciYearCode,
          Zone: tr.ZoneId,
          Lom: tr.LomId,
          Member: tr.MemberId,
          Designation: tr.DesignationId,
          YearwiseOfficesHeld: tr.YearwiseOfficesHeldId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        // thisIns.pageLoadingOn()

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
// Color Palette
$primary-color: #667eea;
$secondary-color: #764ba2;
$gradient-main: linear-gradient(
  135deg,
  $primary-color 0%,
  $secondary-color 100%
);
$success-color: #10b981;
$danger-color: #f44336;
$card-shadow: 0 16px 48px rgba(102, 126, 234, 0.15);

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes cardSlideIn {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shiny {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

#jci-program-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;
  animation: fadeIn 0.6s ease-in-out;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: $gradient-main !important;
    border-radius: 16px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);

    .header-gradient-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: $gradient-main;
      opacity: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 1;
    }
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
  }

  .header-title {
    font-size: 32px;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.8s ease-out;
    letter-spacing: 0.5px;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.95;
    animation: fadeInUp 1s ease-out;
    letter-spacing: 0.3px;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // STATS TEXT
  // ============================================
  .stats-text {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: #333;
    animation: slideInLeft 0.8s ease-out;

    .stats-text-icon {
      color: $primary-color;
      flex-shrink: 0;
    }

    .stats-text-label {
      font-weight: 600;
      color: #555;
      margin-right: 8px;
    }

    .stats-text-count {
      font-weight: 700;
      color: $primary-color;
      font-size: 20px;
    }
  }

  // ============================================
  // STATS ALERT (ENHANCED)
  // ============================================
  .stats-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(20, 177, 250, 0.12) 0%,
      rgba(119, 235, 255, 0.08) 100%
    ) !important;
    border-left: 5px solid #2196f3 !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(33, 150, 243, 0.15) !important;
    padding: 16px 20px !important;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.08) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }

    ::v-deep {
      .v-alert__icon {
        color: #2196f3 !important;
        align-self: flex-start !important;
        margin-top: 0px !important;
        font-size: 22px !important;
      }

      .v-alert__content {
        position: relative;
        z-index: 1;
      }
    }
  }

  .alert-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .alert-title {
    font-size: 11px;
    font-weight: 700;
    color: #2196f3;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
  }

  .alert-number {
    font-size: 32px;
    font-weight: 800;
    color: #1976d2;
    margin: 4px 0 2px 0;
    line-height: 1;
  }

  .alert-subtitle {
    font-size: 11px;
    color: #1565c0;
    margin: 0;
    opacity: 0.9;
    font-weight: 500;
    letter-spacing: 0.2px;
  }

  // ============================================
  // EVENTS SECTION
  // ============================================
  .events-section {
    animation: fadeInUp 1s ease-out 0.2s backwards;
  }

  // ============================================
  // EVENT CARD
  // ============================================
  .event-card {
    border-radius: 14px !important;
    overflow: hidden;
    border: none;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    animation: cardSlideIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    background: white !important;
    min-height: 420px;
    display: flex;
    flex-direction: column;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.3) 50%,
        transparent 100%
      );
      background-size: 1000px 100%;
      animation: shiny 3s infinite;
      pointer-events: none;
      z-index: 1;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 6px;
      background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #31ffe4 100%);
    }

    &:hover {
      transform: translateY(-16px) scale(1.02);
      box-shadow: 0 28px 80px rgba(102, 126, 234, 0.4);

      .event-card-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        box-shadow: 0 12px 30px rgba(102, 126, 234, 0.3);
      }

      .event-icon-box {
        transform: scale(1.15) rotate(5deg);
      }

      .event-title {
        color: $primary-color;
      }
    }

    .event-card-header {
      background: linear-gradient(
        135deg,
        rgba(102, 126, 234, 0.12) 0%,
        rgba(118, 75, 162, 0.12) 100%
      );
      border-bottom: 4px solid $primary-color;
      padding: 24px 24px 20px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: all 0.3s ease;
      position: relative;
    }

    .event-icon-box {
      background: $gradient-main;
      border-radius: 50%;
      width: 60px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
      transition: all 0.3s ease;
      flex-shrink: 0;
    }

    .event-month-badge {
      background: linear-gradient(
        135deg,
        $primary-color 0%,
        $secondary-color 100%
      );
      color: white;
      border-radius: 50%;
      width: 56px;
      height: 56px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 12px;
      box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
      text-transform: uppercase;
    }

    .event-card-content {
      flex-grow: 1;
      padding: 28px 24px;

      .event-title {
        font-size: 19px;
        font-weight: 800;
        color: #1a202c;
        line-height: 1.4;
        letter-spacing: 0.3px;
        word-break: break-word;
        transition: all 0.3s ease;
        margin-bottom: 20px;
      }

      .event-detail {
        display: flex;
        align-items: flex-start;
        font-size: 13px;
        color: #555;
        line-height: 1.8;
        margin-bottom: 14px;
        padding: 10px;
        background: rgba(102, 126, 234, 0.05);
        border-radius: 8px;
        border-left: 3px solid $primary-color;

        .detail-icon {
          color: $primary-color;
          flex-shrink: 0;
          margin-top: 2px;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .detail-label {
          font-weight: 700;
          color: #333;
          min-width: 70px;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
        }

        .detail-value {
          flex-grow: 1;
          color: #666;
          word-break: break-word;
          font-weight: 600;
        }
      }
    }

    .event-card-actions {
      padding: 20px 24px 24px 24px;
      border-top: 2px solid rgba(102, 126, 234, 0.1);
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: rgba(102, 126, 234, 0.02);

      .action-link {
        text-decoration: none;
      }

      .action-btn {
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        border-radius: 8px;
        font-size: 12px !important;
        padding: 12px 16px !important;

        &:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.25);
        }

        .v-icon {
          font-size: 18px !important;
        }
      }

      .register-btn {
        background: linear-gradient(
          135deg,
          $primary-color 0%,
          $secondary-color 100%
        ) !important;

        &:hover {
          box-shadow: 0 12px 28px rgba(102, 126, 234, 0.5) !important;
        }
      }

      .edit-btn {
        background: linear-gradient(
          135deg,
          $success-color 0%,
          #059669 100%
        ) !important;

        &:hover {
          box-shadow: 0 12px 28px rgba(16, 185, 129, 0.5) !important;
        }
      }

      .delete-btn {
        background: linear-gradient(
          135deg,
          $danger-color 0%,
          #d32f2f 100%
        ) !important;

        &:hover {
          box-shadow: 0 12px 28px rgba(244, 67, 54, 0.5) !important;
        }
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 1200px) {
    .header-title {
      font-size: 26px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .event-title {
      font-size: 16px !important;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 10px;

    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 12px;
    }

    .header-icon-box {
      width: 48px;
      height: 48px;
      padding: 10px;
    }

    .action-btn {
      font-size: 12px !important;
      padding: 8px 12px !important;

      .v-icon {
        font-size: 16px !important;
      }
    }

    .stats-card {
      margin-bottom: 16px;
    }

    .event-card {
      min-height: 360px;

      .event-card-content {
        padding: 16px;

        .event-title {
          font-size: 15px !important;
          margin-bottom: 12px;
        }
      }

      .event-card-actions {
        padding: 12px 16px;
        gap: 6px;

        .action-btn {
          font-size: 11px !important;
          padding: 6px 8px !important;

          .v-icon {
            font-size: 16px !important;
          }
        }
      }
    }
  }

  @media (max-width: 600px) {
    padding: 15px 8px;

    .header-card {
      margin-bottom: 12px;

      ::v-deep .v-card__text {
        padding: 12px !important;
      }
    }

    .header-title {
      font-size: 18px;
    }

    .header-subtitle {
      font-size: 11px;
    }

    .header-icon-box {
      width: 40px;
      height: 40px;
      padding: 8px;

      ::v-deep .v-icon {
        font-size: 28px !important;
      }
    }

    .action-btn {
      font-size: 10px !important;
      padding: 6px 10px !important;

      .v-icon {
        font-size: 14px !important;
      }
    }

    .stats-card {
      margin-bottom: 12px;
    }

    .event-card {
      min-height: auto;

      .event-card-header {
        padding: 16px;
      }

      .event-month-badge {
        width: 44px;
        height: 44px;
        font-size: 11px;
      }

      .event-card-content {
        padding: 12px;

        .event-title {
          font-size: 14px !important;
          margin-bottom: 8px;
        }

        .event-detail {
          font-size: 12px;
          margin-bottom: 6px !important;
        }
      }

      .event-card-actions {
        padding: 8px 12px;
        gap: 4px;

        .action-btn {
          font-size: 10px !important;
          padding: 6px 8px !important;

          .v-icon {
            font-size: 14px !important;
            margin-right: 2px !important;
          }
        }
      }
    }
  }

  // ============================================
  // UTILITY CLASSES
  // ============================================
  .page-container {
    width: 100%;
  }

  .gap-2 {
    gap: 8px;
  }

  .text-right {
    text-align: right;
  }

  .justify-end {
    justify-content: flex-end;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .ml-6 {
    margin-left: 24px;
  }

  .ml-4 {
    margin-left: 16px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .mb-3 {
    margin-bottom: 12px;
  }

  .mb-2 {
    margin-bottom: 8px;
  }

  .mr-2 {
    margin-right: 8px;
  }

  .py-12 {
    padding-top: 48px;
    padding-bottom: 48px;
  }

  .mt-4 {
    margin-top: 16px;
  }

  .pa-8 {
    padding: 32px;
  }

  .pa-6 {
    padding: 24px;
  }

  .pa-4 {
    padding: 16px;
  }

  .d-flex {
    display: flex;
  }

  .align-center {
    align-items: center;
  }

  .white--text {
    color: white;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .mb-1 {
    margin-bottom: 4px;
  }

  .mb-0 {
    margin-bottom: 0;
  }

  .font-weight-bold {
    font-weight: 700;
  }

  .text-subtitle2 {
    font-size: 14px;
  }

  .text-h6 {
    font-size: 20px;
  }

  .text-h4 {
    font-size: 34px;
  }

  .primary--text {
    color: $primary-color;
  }
  .center-content {
    display: flex;
    align-items: center; /* vertical center */
    justify-content: center; /* horizontal center */
    text-align: center;
  }
}
</style>